/** Domain types shared by the React app and the Pages Functions. */

export const WORK_TYPES = [
  'Plan',
  'Design',
  'Dev',
  'Multi',
  'Documentation',
  'Meeting',
  'Other',
] as const;

export type WorkType = (typeof WORK_TYPES)[number];

export interface Project {
  id: string;
  name: string;
  client: string | null;
  color: string;
  /** NULL means "inherit the account default rate". */
  rate_inr: number | null;
  archived: 0 | 1;
  created_at: string;
}

export interface Entry {
  id: string;
  project_id: string;
  work_date: string; // YYYY-MM-DD
  title: string;
  work_type: WorkType;
  minutes: number;
  remark: string | null;
  created_at: string;
  updated_at: string;
}

/** An entry joined with the display fields of its project. */
export interface EntryRow extends Entry {
  project_name: string;
  project_color: string;
  /** Effective rate after falling back to the account default. */
  rate_inr: number;
  amount_inr: number;
}

export interface Settings {
  default_rate_inr: number;
  week_starts_on: number;
  owner_name: string | null;
}

export const SHARE_COLUMNS = [
  'work_date',
  'project',
  'title',
  'work_type',
  'minutes',
  'remark',
  'amount',
] as const;

export type ShareColumn = (typeof SHARE_COLUMNS)[number];

export const COLUMN_LABELS: Record<ShareColumn, string> = {
  work_date: 'Date',
  project: 'Project',
  title: 'Title',
  work_type: 'Type of work',
  minutes: 'Hours',
  remark: 'Remark',
  amount: 'Amount (INR)',
};

export type ShareRange =
  | { mode: 'rolling'; days: number }
  | { mode: 'fixed'; from: string; to: string };

export interface ShareConfig {
  /** Empty array means "every project". */
  projectIds: string[];
  range: ShareRange;
  columns: ShareColumn[];
  showTotals: boolean;
  /**
   * Master gate for money. When false the server emits no rate and no amount
   * anywhere in the payload — the `amount` column is dropped rather than hidden.
   */
  showAmounts: boolean;
}

export interface Share {
  id: string;
  label: string;
  config: ShareConfig;
  created_at: string;
  expires_at: string | null;
  revoked: 0 | 1;
}

export interface Totals {
  minutes: number;
  amount_inr: number;
  entry_count: number;
}

export interface SummaryBucket {
  key: string;
  minutes: number;
  amount_inr: number;
}

export interface Summary {
  buckets: SummaryBucket[];
  byType: { work_type: WorkType; minutes: number }[];
  byProject: { project_id: string; project_name: string; project_color: string; minutes: number; amount_inr: number }[];
  totals: Totals;
}
